import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { Page, PageMetadata } from '@tran-monorepo/interfaces';
import { SubjectArea } from '@tran-monorepo/interfaces/applications';
import { EMPTY, Observable } from 'rxjs';
import { catchError, expand, finalize, takeWhile, tap } from 'rxjs/operators';
import { SubjectAreasService } from '../../services';
import { LoadAllSubjectAreas, LoadMoreSubjectAreas, LoadSubjectAreas } from './subject-areas.actions';

export interface SubjectAreasStateModel {
    loading: boolean;
    subjectAreas: SubjectArea[];
    metadata: PageMetadata | null;
    pageSize: number;
}

@State<SubjectAreasStateModel>({
    name: 'subjectAreas',
    defaults: {
        loading: false,
        subjectAreas: [],
        metadata: null,
        pageSize: 20
    }
})
@Injectable()
export class SubjectAreasState {
    private readonly subjectAreasService = inject(SubjectAreasService);

    @Selector()
    static subjectAreas(state: SubjectAreasStateModel): SubjectArea[] {
        return state.subjectAreas;
    }

    @Selector()
    static loading(state: SubjectAreasStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static metadata(state: SubjectAreasStateModel): PageMetadata | null {
        return state.metadata;
    }

    @Action(LoadSubjectAreas)
    load(
        ctx: StateContext<SubjectAreasStateModel>,
        { payload: { pageSize } }: LoadSubjectAreas
    ): Observable<Page<SubjectArea>> {
        ctx.patchState({ loading: true, pageSize });

        return this.subjectAreasService.getSubjectAreas(0, pageSize).pipe(
            tap(({ items: subjectAreas, metadata }) =>
                ctx.patchState({
                    subjectAreas,
                    metadata
                })
            ),
            catchError(() => {
                ctx.patchState({ subjectAreas: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadMoreSubjectAreas)
    loadMore(ctx: StateContext<SubjectAreasStateModel>): Observable<Page<SubjectArea>> | void {
        const { loading, metadata, pageSize } = ctx.getState();
        if (loading || !metadata?.hasNextPage) {
            return;
        }

        const nextPage = (metadata?.currentPage ?? 0) + 1;
        ctx.patchState({ loading: true });

        return this.subjectAreasService.getSubjectAreas(nextPage, pageSize).pipe(
            tap(({ items: newSubjectAreas, metadata }) =>
                ctx.setState(
                    patch<SubjectAreasStateModel>({
                        subjectAreas: append<SubjectArea>(newSubjectAreas),
                        metadata
                    })
                )
            ),
            catchError(() => {
                ctx.patchState({ loading: false });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadAllSubjectAreas)
    loadAll(ctx: StateContext<SubjectAreasStateModel>): Observable<void> {
        ctx.patchState({ loading: true, subjectAreas: [], metadata: null });

        return ctx.dispatch(new LoadSubjectAreas({ pageSize: 100 })).pipe(
            expand(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ? ctx.dispatch(new LoadMoreSubjectAreas()) : EMPTY;
            }),
            takeWhile(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ?? false;
            }, true),
            catchError(() => {
                ctx.patchState({ loading: false, subjectAreas: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }
}
